import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Checkbox, Alert, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { setEventListeners, unsetEventListeners, loadTsepManifestScript } from '../../helpers/tsepUtils';
import '../../css/tsep.css';
import MaskedCard from './MaskedCard';
import { setTsepAuth } from '../../actions/LocationAction';
import autoFillMultipassFields from '../../helpers/functions/autoFillCardFields';

// Must be always displayed on page. TsepAuth will be reset otherwise.
// showCard/showCardStyle enables using this where you need to.

const FormVariants = {
  block: 'block',
  simple: 'simple',
};

const saveAndUpdateChecks = (
  isOneTimeCustomer,
  updateCard,
  customerToken,
  handleChangeCheckbox,
  customerTokenUpdate,
  isRefund
) => {
  if (!isOneTimeCustomer && !isRefund) {
    if (!updateCard) {
      return updateCheckbox('customerToken', customerToken, handleChangeCheckbox, 'Save card for future payments');
    }
    return updateCheckbox(
      'customerTokenUpdate',
      customerTokenUpdate,
      handleChangeCheckbox,
      'Update card for future payments'
    );
  } else {
    return null;
  }
};

const updateCheckbox = (id, checkedValue, handler, label) => {
  return (
    <>
      <Checkbox
        id={id}
        labels="creatNewUser"
        checked={checkedValue}
        onChange={(e) => handler(e)}
        value={checkedValue}
        color="primary"
        inputProps={{
          'aria-label': 'secondary checkbox',
        }}
      />
      <label>{label}</label>
    </>
  );
};

const TsepCardError = () => {
  return (
    <div>
      <Alert variant="outlined" severity="error">
        Payment form failed to load due to a configuration issue. Please contact techsupport@titaniumpayments.com or
        call 877-972-0700.
      </Alert>
    </div>
  );
};

const TsepFormSimple = (isShowingForm) => {
  autoFillMultipassFields();

  return (
    <div className={isShowingForm ? 'block' : 'none'}>
      <div className={'simple-card-form'}>
        <form method="POST" id="payment-form">
          <div id="tsep-cardContainer">
            <div id="tsep-cardNumDiv" data-auto-formatting="Y" data-validate-cc="Y" data-detect-card-type="Y"></div>
          </div>
          <div id="tsep-CardData">
            <div id="tsep-datepickerDiv" data-validate-expiry-date="Y"></div>
            <div id="tsep-cvv2Div" data-validate-cvv2="Y"></div>
          </div>
          <div id="tsep-cardHolderNameDiv" data-validate-name="Y" style={{ display: 'none' }}></div>
          <div id="tsep-zipCodeDiv" data-validate-zipcode="Y" style={{ display: 'none' }}></div>
        </form>
      </div>
    </div>
  );
};

const TsepForm = (
  updateCard,
  customerToken,
  customerTokenUpdate,
  handleChangeCheckbox,
  isOneTimeCustomer,
  isRefund,
  isMobile,
  isShowingForm
) => {
  const cardDetailsWidth = isMobile ? '57%' : '50%';

  autoFillMultipassFields();

  return (
    <div
      className={isShowingForm ? 'block' : 'none'}
      style={{ background: 'linear-gradient(90deg,#4c8390,#238ca1 3%,#345da9 61%)', color: '#fff', borderRadius: 5 }}
    >
      <div className="card-header">
        <Typography variant="h6" component="h2">
          Card Details
        </Typography>
      </div>
      <form style={{ padding: 10, width: '100%' }} method="POST" id="payment-form">
        <div style={{ marginTop: 10, width: cardDetailsWidth }} className="card-details0">
          <label className="card-labels" id="card-label-id">
            Card Number
          </label>
          <div
            id="tsep-cardNumDiv"
            className="tsep-cardNumDiv"
            data-auto-formatting="Y"
            data-validate-cc="Y"
            data-detect-card-type="Y"
          ></div>
        </div>
        <div style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
          <div className="card-details0">
            <label className="card-labels" id="card-label-id">
              Exp Date
            </label>
            <div id="tsep-datepickerDiv" className="tsep-datepickerDiv" data-validate-expiry-date="Y"></div>
          </div>
          <div style={{ marginLeft: 10 }} className="card-details0">
            <label className="card-labels" id="card-label-id">
              CVV
            </label>
            <div id="tsep-cvv2Div" className="tsep-cvv2Div" data-validate-cvv2="Y"></div>
          </div>
        </div>
        <div style={{ marginTop: 10, display: 'none' }}>
          <div id="tsep-cardHolderNameDiv" data-validate-name="Y"></div>
        </div>
        <div style={{ marginTop: 10, display: 'none' }}>
          <div id="tsep-zipCodeDiv" data-validate-zipcode="Y"></div>
        </div>

        {saveAndUpdateChecks(
          isOneTimeCustomer,
          updateCard,
          customerToken,
          handleChangeCheckbox,
          customerTokenUpdate,
          isRefund
        )}
      </form>
    </div>
  );
};

const TsepCard = (props) => {
  const {
    handleChangeCheckbox,
    customerToken,
    customerTokenUpdate,
    updateCard,
    isOneTimeCustomer = false,
    isRefund = false,
    isMobile = false,
    displayCardDetails = { showCardInput: true, headerText: 'Save Card Details', variant: 'block' },
    handleClickAddNewCard,
  } = props;
  const dispatch = useDispatch();
  const tsepAuth = useSelector((state) => state.LocationReducer?.tsepAuth);

  function getDisplayStatus() {
    let showCard = 'none'; // removes element
    if (displayCardDetails?.showCardInput) showCard = 'block';
    return { display: showCard };
  }

  useEffect(() => {
    loadTsepManifestScript().then((auth) => {
      dispatch(setTsepAuth(auth));
    });
    setEventListeners();

    return () => {
      dispatch(setTsepAuth(false));
      unsetEventListeners();
    };
  }, []);

  return (
    <Grid item container>
      <Grid data-cy="tsep-card" item xs={12} sm={12} md={12} style={getDisplayStatus()}>
        {/* NOTE: The logic to determine if the TsepForm is displayed or the MaskedCard must 
            be this way in order for the TsepForm to always be on the page. Otherwise the tsepAuth
            will not work correctly if the form is taken off and readded to the rendered components
        */}
        {tsepAuth ? (
          displayCardDetails?.variant === FormVariants.simple ? (
            <>{TsepFormSimple(displayCardDetails?.showCardInput)}</>
          ) : (
            <>
              {TsepForm(
                updateCard,
                customerToken,
                customerTokenUpdate,
                handleChangeCheckbox,
                isOneTimeCustomer,
                isRefund,
                isMobile,
                displayCardDetails?.showCardInput
              )}
            </>
          )
        ) : (
          <>{TsepCardError()}</>
        )}
      </Grid>
      {!displayCardDetails?.showCardInput && (
        <Grid item xs={12} sm={12} md={12}>
          <MaskedCard
            headerText={displayCardDetails?.headerText ?? 'Saved Card Details'}
            cardDetails={{
              accountType: displayCardDetails?.accountType,
              maskedCardNumber: displayCardDetails?.maskedCardNumber,
            }}
            onClickAddNewCard={handleClickAddNewCard}
          />
        </Grid>
      )}
    </Grid>
  );
};

TsepCard.propTypes = {
  handleChangeCheckbox: PropTypes.any,
  customerToken: PropTypes.any,
  customerTokenUpdate: PropTypes.any,
  updateCard: PropTypes.any,
  isOneTimeCustomer: PropTypes.any,
  isRefund: PropTypes.bool,
  isMobile: PropTypes.any,
  displayCardDetails: PropTypes.shape({
    showCardInput: PropTypes.bool,
    headerText: PropTypes.string,
    accountType: PropTypes.string,
    maskedCardNumber: PropTypes.string,
    variant: PropTypes.string,
  }),
  handleClickAddNewCard: PropTypes.func,
};

export default TsepCard;
